import { cva } from 'class-variance-authority';

/**
 * Radio class recipes.
 *
 * Zero `dark:` variants, as everywhere else in this layer — colour comes entirely from the
 * `--ds-radio-*` slots that `.ds-radio` sets per theme in `components/styles/design-system.css`.
 *
 * ── The geometry ───────────────────────────────────────────────────────────────────────────────
 *
 * Read off the exported vector, and it is the checkbox's geometry with the corners taken off:
 *
 *   a 24x24 area                    the hit target, and the focus halo (`rx 12` — a circle)
 *   a circle at r 8.25              with a 1.5px CENTRED stroke, so it spans 3 -> 21
 *   a circle at r 5                 the selected dot, 10px across
 *
 * A centred stroke is half in and half out, so in CSS that is an 18px round border-box inset 3px
 * inside the 24px control, with a 10px dot centred in it. Identical numbers to the checkbox, which
 * is what keeps a radio and a checkbox on the same baseline in the survey forms the design pairs
 * them in.
 *
 * ── WARNING: the focus indicator is weaker than the checkbox's ─────────────────────────────────
 *
 * Focus adds the halo and NOTHING ELSE. The stroke keeps its resting colour in every focused cell —
 * verified on all four (`Checked=False/True` x `Mode=Dark/Light`). That differs from the checkbox,
 * whose border turns Blue/Primary on focus, and it matters: the halo is Blue/Primary at 20% over
 * white in light mode and 50% over near-black in dark, neither of which reaches the 3:1 an
 * indicator needs for WCAG 1.4.11. On the checkbox the stroke change carried that contrast; here
 * there is nothing to carry it.
 *
 * Reproduced as authored rather than quietly strengthened, because inventing a focus treatment
 * pre-empts a designer who has demonstrably specified one elsewhere. Raised as the first open
 * question on HNS-333. If the answer is "the ring should turn blue too", it is one selector.
 *
 * ── How the states are selected ────────────────────────────────────────────────────────────────
 *
 * `group/rd` sits on `Radio.Root`, which carries `data-checked` / `data-unchecked` / `data-disabled`
 * and is also the focusable element, so one group serves everything and no colour change is driven
 * by React state.
 *
 * A radio has no mixed state, so unlike the checkbox `data-checked` can be matched directly rather
 * than pivoting on the absence of `data-unchecked`. Selectors are mutually exclusive rather than
 * order-dependent, as in the sibling recipes. Precedence: disabled -> selected -> resting, with
 * hover orthogonal to all three because it only ever touches the fill.
 */

/**
 * The wrapper, a real `<label>`. Cursor is set here and inherited, so ring and text agree.
 *
 * `items-start`, not `items-center`: a label long enough to wrap must leave the ring on the first
 * line rather than dragging it down to the centre of the whole text block. Survey answers are the
 * place this bites — they are full sentences at a narrow column width. See the note on
 * `radioLabelRecipe` for the 2px that keeps a single line looking exactly as it did.
 */
export const radioRootRecipe = cva(['ds-radio', 'inline-flex w-fit items-start gap-[8px] py-[2px]'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * `Radio.Root` — the 24px hit area, the focus halo, and the group the other recipes read.
 *
 * `focus-visible` rather than `focus`, so the halo answers a keyboard tab and not a mouse click —
 * the same call the checkbox makes, and the same reason: a halo left on every option the user merely
 * picks is noise. Inside a group this matters more than it does for a checkbox, because arrow-keying
 * through the options moves both the selection and the focus.
 */
export const radioControlRecipe = cva([
  'group/rd',
  'relative block size-[24px] shrink-0 cursor-[inherit] rounded-full',
  'bg-transparent outline-none',
  'focus-visible:bg-[var(--ds-radio-halo)]',
  'transition-colors duration-150 motion-reduce:transition-none',
]);

/**
 * The ring — a child of the root, because the halo is a 24px circle and the ring an 18px one.
 *
 * Unfilled at rest in BOTH themes, which is a real difference from the checkbox rather than an
 * omission: the light checkbox sits on Light/0, the radio on nothing. The upshot is that the halo
 * reads through a focused radio in either theme.
 */
export const radioRingRecipe = cva([
  'pointer-events-none absolute inset-[3px] rounded-full',
  'border-[length:1.5px] border-solid bg-transparent',
  'transition-[border-color,background-color] duration-150 motion-reduce:transition-none',

  // Unselected and enabled — the base case, out-specified by everything below.
  'border-[var(--ds-radio-border)]',

  'group-[[data-unchecked][data-disabled]]/rd:border-[var(--ds-radio-border-disabled)]',
  'group-[[data-checked]:not([data-disabled])]/rd:border-[var(--ds-radio-on)]',
  'group-[[data-checked][data-disabled]]/rd:border-[var(--ds-radio-on-disabled)]',

  // Hover fills, selected or not. One rule because the design tints both the same way — the only
  // state in this component that is indifferent to whether the option is chosen.
  'group-[:not([data-disabled]):hover]/rd:bg-[var(--ds-radio-bg-hover)]',
]);

/**
 * `Radio.Indicator` — the dot, mounted only while selected.
 *
 * Centred by `m-auto` against `inset-0` rather than by a translate, so it cannot drift half a pixel
 * on a fractional layout the way a percentage offset can.
 */
export const radioDotRecipe = cva([
  'pointer-events-none absolute inset-0 m-auto size-[10px] rounded-full',
  'bg-[var(--ds-radio-on)]',
  'group-data-[disabled]/rd:bg-[var(--ds-radio-on-disabled)]',
]);

/**
 * The label text.
 *
 * `py-[2px]` is what makes `items-start` on the wrapper safe, exactly as on the checkbox: 14px text
 * on a 20px line against a 24px ring would top-align 2px high, and 2px of padding grows the FIRST
 * LINE BOX to 24px so it centres against the ring. A single-line radio therefore renders identically
 * to the `items-center` version this replaces. `LongLabel` measures it.
 *
 * Both colours are variants rather than a base plus an override, since two bare `text-*` utilities
 * would leave the winner to Tailwind's output order.
 */
export const radioLabelRecipe = cva(['font-sans text-body-md select-none py-[2px]'], {
  variants: {
    disabled: {
      true: 'text-[var(--ds-radio-label-disabled)]',
      false: 'text-[var(--ds-radio-label)]',
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * The group.
 *
 * A column with the same 8px rhythm the design's answer lists use (node 586:38132, where the rows
 * sit 38px apart on a 28px row). Layout only — the group holds no colour of its own, and the
 * `.ds-radio` slot class stays on each option so a caller can put one radio anywhere.
 */
export const radioGroupRecipe = cva(['flex flex-col gap-[8px]']);
